import { parseIni } from './ini';

/**
 * Loads the application's configuration from config/app.ini.
 *
 * Fetched at run time rather than compiled in, because the app is deployed by
 * uploading a built bundle: repointing it at a different API host, or turning a
 * flag off, is then an edit to one text file on the server instead of a
 * rebuild. The file sits in the app's public/ folder, so it is served next to
 * index.html and can be replaced in place.
 *
 * The whole application waits on this before its first render (see main.jsx).
 * That is deliberate — a component that reads configuration must never see a
 * half-populated object, and making the load a precondition of rendering is
 * simpler to reason about than a "config is ready" flag threaded through the
 * component tree.
 */

/** Where the file is fetched from, relative to the app's own origin. */
export const CONFIG_URL = 'config/app.ini';

/** Sections and keys that must be present for the app to function at all. */
const REQUIRED_KEYS = [
  ['api', 'basePath'],
  ['api', 'executePath']
];

const DEFAULTS = {
  api: {
    baseUrl: '',
    basePath: '/api/v1',
    executePath: '/execute',
    timeoutMs: 15000
  },
  security: {
    encryptPayloads: true
  },
  session: {
    storage: 'local',
    accessTokenKey: 'srichaitra.accessToken',
    refreshTokenKey: 'srichaitra.refreshToken'
  },
  ui: {
    appName: 'SriChaitra',
    theme: 'ocean'
  }
};

let config = null;
let loadPromise = null;

/** Section-wise merge: a file that omits a section still gets its defaults. */
function withDefaults(parsed) {
  const merged = {};

  for (const section of Object.keys(DEFAULTS)) {
    merged[section] = { ...DEFAULTS[section], ...(parsed[section] || {}) };
  }

  // Sections the file adds that we have no defaults for are kept as-is, so the
  // file can carry settings this build does not know about yet.
  for (const section of Object.keys(parsed)) {
    if (!merged[section]) merged[section] = parsed[section];
  }

  return merged;
}

function assertUsable(candidate) {
  for (const [section, key] of REQUIRED_KEYS) {
    const value = candidate[section]?.[key];
    if (value === undefined || value === '') {
      throw new Error(`config/app.ini is missing [${section}] ${key}`);
    }
  }
}

/**
 * Fetches and parses the configuration once.
 *
 * Concurrent callers share the in-flight request. A failed load rejects rather
 * than falling back to the defaults: the defaults describe a shape, not a
 * working deployment, and an app that silently runs against the wrong API is
 * worse than one that refuses to start.
 */
export function loadAppConfig() {
  if (config) return Promise.resolve(config);
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      // cache: 'no-store' so a redeployed .ini is picked up on the next load
      // rather than being served from the browser cache alongside a new bundle.
      const response = await fetch(CONFIG_URL, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Could not load ${CONFIG_URL} (HTTP ${response.status})`);
      }

      const parsed = withDefaults(parseIni(await response.text()));
      assertUsable(parsed);

      config = parsed;
      return config;
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/**
 * The loaded configuration.
 *
 * Synchronous, and throws if called before loadAppConfig() has resolved — a
 * loud failure during development beats an undefined that spreads quietly into
 * a request URL.
 */
export function getAppConfig() {
  if (!config) {
    throw new Error('Configuration has not been loaded yet — await loadAppConfig() first');
  }
  return config;
}

/** The full URL of the dispatch endpoint every API call goes through. */
export function getExecuteUrl() {
  const { baseUrl, basePath, executePath } = getAppConfig().api;
  return `${baseUrl}${basePath}${executePath}`;
}

/** Replaces the configuration in tests. Not used by application code. */
export function __setAppConfigForTests(value) {
  config = value === null ? null : withDefaults(value);
}
